// DeadLetterQueue — failed payment events after max retry attempts
import { useCallback } from "react";
import {
  Loader2,
  CheckCircle,
  RotateCcw,
  Trash2,
  AlertTriangle,
} from "lucide-react";
import AdminLayout from "@/components/admin/AdminLayout";
import { ModuleGuard } from "@/components/admin/modules/ModuleGuard";
import {
  useDeadLetterQuery,
  useRetryDeadLetter,
  useDismissDeadLetter,
} from "@/hooks/admin/usePayments";
import type { DeadLetterEntry } from "@/lib/admin-api/payment";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDateTime(iso: string): string {
  const d = new Date(iso);
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  const hours = String(d.getHours()).padStart(2, "0");
  const minutes = String(d.getMinutes()).padStart(2, "0");
  return `${day}/${month}/${year} ${hours}:${minutes}`;
}

function truncate(s: string | null | undefined, len: number): string {
  if (!s) return "—";
  return s.length > len ? `${s.slice(0, len)}…` : s;
}

// ---------------------------------------------------------------------------
// Attempts badge
// ---------------------------------------------------------------------------

function AttemptsBadge({ count }: { readonly count: number }) {
  const color =
    count >= 5
      ? "bg-red-100 text-red-800"
      : count >= 3
        ? "bg-amber-100 text-amber-800"
        : "bg-stone-100 text-stone-700";
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${color}`}
    >
      {count}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Row component
// ---------------------------------------------------------------------------

interface RowProps {
  readonly entry: DeadLetterEntry;
  readonly onRetry: (id: string) => void;
  readonly onDismiss: (id: string) => void;
  readonly isRetrying: boolean;
  readonly isDismissing: boolean;
}

function DeadLetterRow({
  entry,
  onRetry,
  onDismiss,
  isRetrying,
  isDismissing,
}: RowProps) {
  const eventType = entry.event?.type ?? "—";

  return (
    <tr className="hover:bg-muted/50 transition-colors">
      <td className="whitespace-nowrap px-4 py-3 text-sm text-stone-700">
        {formatDateTime(entry.createdAt)}
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0 text-amber-500" />
          <span className="font-mono text-xs text-stone-700">{eventType}</span>
        </div>
      </td>
      <td className="px-4 py-3">
        <AttemptsBadge count={entry.attempts} />
      </td>
      <td className="px-4 py-3 max-w-xs">
        <span
          className="text-xs text-muted-foreground"
          title={entry.lastError ?? undefined}
        >
          {truncate(entry.lastError, 50)}
        </span>
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-1">
          <button
            onClick={() => onRetry(entry.id)}
            disabled={isRetrying || isDismissing}
            aria-label="Réessayer"
            title="Réessayer"
            className="rounded-md p-2 hover:bg-muted transition-colors disabled:cursor-not-allowed disabled:opacity-40"
          >
            {isRetrying ? (
              <Loader2 className="h-4 w-4 animate-spin text-stone-500" />
            ) : (
              <RotateCcw className="h-4 w-4 text-stone-500 hover:text-stone-800" />
            )}
          </button>
          <button
            onClick={() => onDismiss(entry.id)}
            disabled={isRetrying || isDismissing}
            aria-label="Supprimer"
            title="Supprimer définitivement"
            className="rounded-md p-2 hover:bg-red-50 transition-colors disabled:cursor-not-allowed disabled:opacity-40"
          >
            {isDismissing ? (
              <Loader2 className="h-4 w-4 animate-spin text-red-400" />
            ) : (
              <Trash2 className="h-4 w-4 text-red-400 hover:text-red-600" />
            )}
          </button>
        </div>
      </td>
    </tr>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function DeadLetterQueue() {
  const { data, isLoading } = useDeadLetterQuery();
  const retryMutation = useRetryDeadLetter();
  const dismissMutation = useDismissDeadLetter();

  const deadLetters = data?.deadLetters ?? [];
  const total = data?.total ?? 0;

  const handleRetry = useCallback(
    (id: string) => {
      retryMutation.mutate(id);
    },
    [retryMutation],
  );

  const handleDismiss = useCallback(
    (id: string) => {
      const confirmed = window.confirm(
        "Supprimer définitivement cet événement ? Cette action est irréversible.",
      );
      if (confirmed) {
        dismissMutation.mutate(id);
      }
    },
    [dismissMutation],
  );

  return (
    <AdminLayout>
      <ModuleGuard moduleKey="wave-callbacks">
        <div className="space-y-6 p-6">
          {/* Header */}
          <div>
            <h1 className="text-xl font-semibold text-stone-900">
              File d&apos;attente des erreurs
            </h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Événements de paiement en échec après tentatives maximales
            </p>
          </div>

          {/* Summary badge */}
          {!isLoading && total > 0 && (
            <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-2.5">
              <AlertTriangle className="h-4 w-4 flex-shrink-0 text-red-500" />
              <p className="text-sm text-red-700">
                <span className="font-semibold">{total}</span> événement
                {total !== 1 ? "s" : ""} en attente de traitement
              </p>
            </div>
          )}

          {/* Table */}
          <div className="overflow-hidden rounded-lg border border-stone-200 bg-white">
            {isLoading ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="h-6 w-6 animate-spin text-stone-400" />
              </div>
            ) : deadLetters.length === 0 ? (
              <div className="flex flex-col items-center justify-center gap-3 py-16 text-center">
                <CheckCircle className="h-10 w-10 text-emerald-400" />
                <p className="font-medium text-stone-700">
                  Aucun événement en erreur
                </p>
                <p className="text-sm text-muted-foreground">
                  Tous les événements ont été traités avec succès
                </p>
              </div>
            ) : (
              <table className="w-full">
                <thead className="border-b border-stone-200 bg-stone-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      Date
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      Type d&apos;événement
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      Tentatives
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      Dernière erreur
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-100">
                  {deadLetters.map((entry) => (
                    <DeadLetterRow
                      key={entry.id}
                      entry={entry}
                      onRetry={handleRetry}
                      onDismiss={handleDismiss}
                      isRetrying={
                        retryMutation.isPending &&
                        retryMutation.variables === entry.id
                      }
                      isDismissing={
                        dismissMutation.isPending &&
                        dismissMutation.variables === entry.id
                      }
                    />
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </ModuleGuard>
    </AdminLayout>
  );
}
